
import { windowatch } from '../../../scripts/lib/windowatch';

const name = 'module-facts-ticker';
const speed = 60 * 7;

// idle time necessary to restart the tape animation
const idleCooldown = 3000;

export default class FactsTickerModule {

  constructor ($el) {

    this.$el = $el;

    // states
    this.enabled = false;
    this.dragX = 0;
    this.position = null;
    this.idleCooldownTimeout = null;

    // measurements
    this.items = null;
    this.itemsWidth = null;
    this.tapeWidth = null;

    // handlers
    this.dragHandler = this.onDrag.bind(this);
    this.dragEndHandler = this.onDragEnd.bind(this);

    // elements
    this.$inner = $el.querySelector(`.${name}__inner`);
    this.$list = $el.querySelector(`.${name}__list`);

    // items
    this.$originalItems = [...$el.querySelectorAll(`.${name}__item`)];
    this.$items = [...this.$originalItems];

    // add event listeners
    this.$inner.addEventListener('mousedown', this.onDragStart.bind(this));
    this.$list.addEventListener('transitionend', this.onTapeTransitionEnd.bind(this));
    windowatch.addResizeListener(this.onResize.bind(this));
  }

  /**
   * Triggered on resize.
   * @param {number} width Window width
   * @param {number} height Window height
   * @return {Function} Update callback
   */
  onResize (width, height) {

    // measure item sizes
    this.itemsWidth = 0;
    this.items = this.$items.map(($item, index) => {
      const rect = $item.getBoundingClientRect();
      const width = rect.width;
      const x = this.itemsWidth;
      this.itemsWidth += rect.width;

      return {
        $item,
        width,
        x,
        position: this.items && this.items[index] ? this.items[index] : null
      };
    });

    // sum up width of original items
    let originalItemsWidth = 0;
    this.$originalItems.forEach(($item, index) => {
      originalItemsWidth += this.items[index].width;
    });

    // measure container size
    this.tapeWidth = this.$inner.getBoundingClientRect().width;

    // measurements are done, do dom interactions in update callback
    return () => {

      // carousel is enabled when original items fit into the container
      this.setEnabled(originalItemsWidth > this.tapeWidth);
    };
  }

  /**
   * Triggered on tape transition end.
   * @param {Event} evt
   */
  onTapeTransitionEnd (evt) {

    if (this.enabled) {
      this.setPosition(this.position + speed);
    }
  }

  /**
   * Enables or disables the sliding animation.
   * @param {boolean} enabled
   */
  setEnabled (enabled) {

    if (this.enabled !== enabled) {
      this.enabled = enabled;

      if (enabled) {

        // duplicate fact items, they may appear twice at the edges
        this.$originalItems.forEach(($item, index) => {
          const $duplicatedItem = $item.cloneNode(true);
          const { width } = this.items[index];
          const x = this.itemsWidth;
          this.itemsWidth += width;
          this.$items.push($duplicatedItem);
          this.items.push({
            $item: $duplicatedItem,
            width,
            x,
            position: null
          });
          this.$list.appendChild($duplicatedItem);
        });

        // set enabled modifier
        this.$el.classList.add(`${name}--enabled`);

        // set initial position
        this.setPosition(speed);

      } else {

        // clean up list style
        this.$list.removeAttribute('style');

        // clean up item styles
        this.$originalItems.forEach($originalItem => {
          $originalItem.removeAttribute('style');
        });

        // remove duplicated items from dom and from the items array
        for (let i = this.$originalItems.length; i < this.$items.length; i++) {
          const $duplicatedItem = this.$items[i];
          const item = this.items[i];
          $duplicatedItem.parentNode.removeChild($duplicatedItem);
          this.itemsWidth -= item.width;
        }

        this.$items.splice(this.$originalItems.length, this.$originalItems.length);
        this.items.splice(this.$originalItems.length, this.$originalItems.length);

        // reset position
        this.position = null;

        // remove enabled modifier
        this.$el.classList.remove(`${name}--enabled`);
      }
    }
  }

  /**
   * Sets the sliding position.
   * @param {number} position
   */
  setPosition (position) {

    if (this.position !== position) {
      const itemsWidth = this.itemsWidth;

      // limit the transition extend by moving the position back to the center
      const delta = (position + itemsWidth) % itemsWidth - position;

      // alter position
      const lastPosition = this.position;
      // eslint-disable-next-line no-param-reassign
      position += delta;
      this.position = position;

      const beginPosition = lastPosition + delta;
      const centerPosition = position + this.tapeWidth * 0.5;

      // the first position on the left side of the tape center,
      // where items get repeated
      const itemBase = parseInt(centerPosition / itemsWidth) * itemsWidth;

      // update item positions of each item
      this.$items.forEach(($item, index) => {
        const item = this.items[index];

        // choose item position closest to the center position of the tape
        const itemPosition = this.chooseClosest(
          centerPosition,
          itemBase - itemsWidth + item.x,
          itemBase + item.x,
          itemBase + itemsWidth + item.x);

        // check if the item position has changed
        if (item.position !== itemPosition) {
          item.position = itemPosition;
          item.$item.style.transform =
            `translate3d(${item.position}px, 0px, 0px)`;
        }
      });

      // translate position before beginning the actual transition, if needed
      if (lastPosition !== beginPosition && lastPosition !== null) {
        this.$list.style.transition = 'none';
        this.$list.style.transform =
          `translate3d(${-beginPosition}px, 0px, 0px)`;

        // trigger CSS reflow
        this.$list.offsetHeight;
        this.$list.style.transition = '';
      }

      // update tape position and trigger transition
      this.$list.style.transform =
        `translate3d(${-position}px, 0px, 0px)`;
    }
  }

  /**
   * Returns the number that is closest to a given target number.
   * @param {number} target Target number
   * @param {...number} integers Numbers to choose from
   * @return {number}
   */
  chooseClosest (target, ...numbers) {

    let delta = Math.abs(target - numbers[0]);
    let nearestNumber = numbers[0];

    for (let i = 1; i < numbers.length; i++) {
      const integerDelta = Math.abs(target - numbers[i]);
      if (integerDelta < delta) {
        delta = integerDelta;
        nearestNumber = numbers[i];
      }
    }

    return nearestNumber;
  }

  /**
   * Triggered on drag start.
   * @param {MouseEvent} evt
   */
  onDragStart (evt) {

    // dragging is only enabled when the animation is enabled, too
    if (this.enabled) {
      evt.preventDefault();
      this.clearIdleCooldown();

      // measure position inside transition
      this.position = -parseInt(this.$list.getBoundingClientRect().left);

      // update tape position without triggering transition
      this.$list.style.transition = '';
      this.$list.style.transform = `translate3d(${-this.position}px, 0px, 0px)`;

      // trigger CSS reflow
      this.$list.offsetHeight;
      this.$list.style.transition = '';

      // track start position
      this.dragX = evt.clientX;

      // add dragging modifiers
      this.$el.classList.add(`${name}--dragging`);

      // add drag listeners
      document.addEventListener('mousemove', this.dragHandler);
      document.addEventListener('mouseup', this.dragEndHandler);
    }
  }

  /**
   * Triggered while dragging.
   * @param {MouseEvent} evt
   */
  onDrag (evt) {

    // move position
    const dragDelta = evt.clientX - this.dragX;
    this.setPosition(this.position - dragDelta);

    // track last drag position
    this.dragX = evt.clientX;
  }

  /**
   * Triggered on drag end.
   * @param {MouseEvent} evt
   */
  onDragEnd (evt) {

    evt.preventDefault();
    this.startIdleCooldown();

    // remove dragging modifier
    this.$el.classList.remove(`${name}--dragging`);

    // remove drag listeners
    document.removeEventListener('mousemove', this.dragHandler);
    document.removeEventListener('mouseup', this.dragEndHandler);
  }

  /**
   * Starts the idle cooldown.
   */
  startIdleCooldown () {

    if (this.idleCooldownTimeout === null) {
      this.idleCooldownTimeout = setTimeout(
        this.onIdleCooldown.bind(this), idleCooldown);
    }
  }

  /**
   * Clears the idle cooldown.
   */
  clearIdleCooldown () {

    if (this.idleCooldownTimeout !== null) {
      clearTimeout(this.idleCooldownTimeout);
      this.idleCooldownTimeout = null;
    }
  }

  /**
   * Triggered when the idle cooldown has completed.
   */
  onIdleCooldown () {

    if (this.enabled) {

      // restart tape animation
      this.setPosition(this.position + speed);
    }
  }
}
